import { DateFieldWithClear } from '@/components/common/DateFieldWithClear';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { fmtDate } from '@/lib/utils';
import type { User } from '@/types';

type Props = {
  users: User[];
  idAutorizado: number | null;
  onIdAutorizadoChange: (id: number | null) => void;
  fechaAutorizado: string;
  onFechaAutorizadoChange: (fecha: string) => void;
  nomAutorizado?: string | null;
  // Cuando true, el componente solo renderiza el dot + combo (sin la fecha
  // interna). El form externo se encarga de poner el DateFieldWithClear
  // por separado en otra columna del grid.
  hideDate?: boolean;
  // Cuando true, muestra solo el nombre del autorizador (sin combo editable).
  // Se usa en el tab Revisión, donde el Revisor se asigna desde Detalles.
  readOnly?: boolean;
};

export function AutorizadoInternoField({
  users, idAutorizado, onIdAutorizadoChange,
  fechaAutorizado, onFechaAutorizadoChange,
  nomAutorizado,
  hideDate = false,
  readOnly = false,
}: Props) {
  const isAutorizado = !!fechaAutorizado;
  const tooltip = isAutorizado
    ? `Autorizado el ${fmtDate(fechaAutorizado)}${nomAutorizado ? ` por ${nomAutorizado}` : ''}`
    : 'No autorizado';

  // Nombre resuelto del autorizador para el modo read-only (busca en la lista
  // si el combo no provee nomAutorizado, ej. en CREATE).
  const nombreResuelto = nomAutorizado
    ?? users.find(u => u.idCol === idAutorizado)?.nombreCompleto
    ?? users.find(u => u.idCol === idAutorizado)?.nombre
    ?? null;

  return (
    <div>
      <label className="text-xs font-medium text-stone-700 mb-1 block">
        Autorizado Interno
      </label>
      <div className="flex items-center gap-2">
        <span
          className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${
            isAutorizado ? 'bg-brand-green-200' : 'bg-stone-300'
          }`}
          title={tooltip}
        />
        {readOnly ? (
          <span className="flex-1 px-3 py-2 text-sm text-stone-700">
            {nombreResuelto || <span className="text-stone-400">— Sin Asignar —</span>}
          </span>
        ) : (
        <SearchableSelect<number>
          options={users.map(u => ({
            value: u.idCol,
            label: u.nombreCompleto || u.nombre || `#${u.idCol}`,
          }))}
          value={idAutorizado}
          onChange={onIdAutorizadoChange}
          placeholder="— Sin Asignar —"
          className="flex-1"
        />
        )}
        {!hideDate && (
          <DateFieldWithClear
            value={fechaAutorizado}
            onChange={onFechaAutorizadoChange}
            className="w-44"
          />
        )}
      </div>
    </div>
  );
}
